import {
  CanvasHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useEvent } from 'react-use'
import gsap from 'gsap'
import { SimplexNoise } from 'three-stdlib'

import { isCorporate } from '@/constants'

import { cn } from '@/lib/utils'
import { FluidProps } from '@/types'

export type FluidLineProps = CanvasHTMLAttributes<HTMLCanvasElement> &
  Partial<FluidProps>

const FluidLine = ({
  segments = 40,
  amplitude = 20,
  period = 0.0009,
  speed = 0.005,
  className,
  ...props
}: FluidLineProps) => {
  const canvas = useRef<HTMLCanvasElement>(null!)
  const context = useRef<CanvasRenderingContext2D | null>(null)
  const [perlin] = useState(() => new SimplexNoise())
  const variation = useRef(0)

  const tick = useCallback(() => {
    if (!context.current) return

    const ctx = context.current
    const width = canvas.current.width
    const height = canvas.current.height
    const segment = width / segments

    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(26, 26, 26, 0.4)'
    ctx.clearRect(0, 0, width, height)
    ctx.beginPath()
    for (let x = 0, u = 0; x <= segments; x++, u += segment) {
      const point =
        perlin.noise(u * period - variation.current, u * period) * amplitude
      ctx.lineTo(x * segment, point + height / 2)
    }
    ctx.stroke()

    variation.current += speed
  }, [segments, amplitude, period, speed, perlin])

  const resize = () => {
    if (!canvas.current) return

    const { width, height } = canvas.current.getBoundingClientRect()
    canvas.current.width = width
    canvas.current.height = height
  }

  useEffect(() => resize(), [])
  useEvent('resize', resize)

  useEffect(() => {
    if (!canvas.current) return

    context.current = canvas.current?.getContext('2d')

    gsap.ticker.add(tick)

    return () => {
      gsap.ticker.remove(tick)
    }
  }, [tick])

  return isCorporate ? (
    <canvas
      ref={canvas}
      className={cn('h-[3.8rem] w-full', className)}
      {...props}
    />
  ) : null
}

export default FluidLine
